import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';

export class TemplateService {
  constructor() {
    this.templateCache = new Map();
    
    // Use absolute paths from public directory
    this.templateFiles = {
      1: '/templates/professional.docx',
      2: '/templates/modern.docx',
      3: '/templates/creative.docx',
      4: '/templates/academic.docx'
    };
  }

  async loadTemplate(templateId) {
    // Check cache first
    if (this.templateCache.has(templateId)) {
      return this.templateCache.get(templateId);
    }

    const templatePath = this.templateFiles[templateId] || this.templateFiles[1];

    try {
      console.log('Loading template from:', templatePath);
      const response = await fetch(templatePath);
      
      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.status} ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      console.log(`Template ${templateId} loaded successfully:`, arrayBuffer.byteLength, 'bytes');
      
      // Cache the template
      this.templateCache.set(templateId, arrayBuffer);
      
      return arrayBuffer;
    } catch (error) {
      console.error('Error loading template:', error);
      throw new Error(`Template loading failed: ${error.message}`);
    }
  }

  // Generate DOCX from template with data replacement
  async generateFromTemplate(templateId, resumeData) {
    try {
      console.log('Generating document for template:', templateId);
      
      // DEBUG: Analyze the incoming resume data
      this.debugResumeData(resumeData);
      
      const templateBuffer = await this.loadTemplate(templateId);
      
      // Check if we got a valid template buffer
      if (!templateBuffer || templateBuffer.byteLength === 0) {
        throw new Error('Template buffer is empty or invalid. Please check if template files exist.');
      }
      
      // Convert ArrayBuffer to PizZip
      const zip = new PizZip(templateBuffer);
      
      // Create docxtemplater instance with updated API
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        nullGetter: () => '',  // Handle null/undefined values
      });

      // Prepare data for template replacement
      const templateData = this.prepareTemplateData(resumeData);
      console.log('Template data prepared:', templateData);

      // Use the correct method - render with data
      try {
        // Render the document (replace placeholders with actual data)
        doc.render(templateData);
      } catch (error) {
        console.error('Template rendering error:', error);
        // Handle template errors gracefully
        throw new Error(`Template rendering failed: ${error.message}`);
      }

      // Generate the final document
      const output = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        compression: 'DEFLATE'
      });

      console.log('Document generated successfully');
      return output;
    } catch (error) {
      console.error('Error generating document from template:', error);
      throw error;
    }
  }

  // FIXED: Helper method to convert skills object/array/string to different formats
 // UPDATED: Helper method to convert skills string to different formats for Word templates
formatSkills(skills) {
  console.log('Formatting skills:', skills, typeof skills);
  
  if (!skills) {
    return {
      skillsArray: [],
      skillsString: '',
      skillsFormatted: '',
      skillsCategories: [],
      skillsList: [],
      skillsBulletPoints: []
    };
  }

  // NEW: Handle the formatted bullet point structure from form
  if (typeof skills === 'string' && skills.includes('• **')) {
    // Parse the bullet point formatted skills
    const skillsArray = [];
    const skillsCategories = [];
    const skillsBulletPoints = [];
    let skillsString = '';
    let skillsFormatted = skills; // Keep original formatting
    
    // Split by lines and process each bullet point
    const lines = skills.split('\n').filter(line => line.trim());
    
    lines.forEach(line => {
      if (line.startsWith('• **')) {
        // Extract category and skills from: • **Category**: skill1, skill2, skill3
        const match = line.match(/• \*\*(.*?)\*\*: (.*)/);
        if (match) {
          const category = match[1];
          const categorySkills = match[2];
          const skillsInCategory = categorySkills.split(',').map(s => s.trim()).filter(s => s);
          
          skillsCategories.push({
            category: category,
            skills: categorySkills,
            skillsArray: skillsInCategory
          });
          
          skillsBulletPoints.push({
            category: category,
            skills: categorySkills,
            formatted: `• ${category}: ${categorySkills}` // For Word template
          });
          
          // Add to flat arrays
          skillsInCategory.forEach(skill => {
            if (skill) skillsArray.push(skill);
          });
          
          // Build string representation
          skillsString += categorySkills + ', ';
        }
      }
    });
    
    // Remove trailing comma
    skillsString = skillsString.replace(/, $/, '');
    
    return {
      skillsArray: skillsArray,
      skillsString: skillsString,
      skillsFormatted: skillsFormatted,
      skillsCategories: skillsCategories,
      skillsList: skillsArray,
      skillsBulletPoints: skillsBulletPoints
    };
  }

  // If skills is a string (simple comma-separated)
  if (typeof skills === 'string') {
    const skillsArray = skills.split(',').map(s => s.trim()).filter(s => s);
    return {
      skillsArray: skillsArray,
      skillsString: skills.trim(),
      skillsFormatted: skills.trim(),
      skillsCategories: [{ category: 'Skills', skills: skills.trim(), skillsArray: skillsArray }],
      skillsList: skillsArray,
      skillsBulletPoints: [{ category: 'Skills', skills: skills.trim(), formatted: skills.trim() }]
    };
  }

  // If skills is an object with categories (from AI parsing)
  if (typeof skills === 'object' && skills !== null && !Array.isArray(skills)) {
    const skillsArray = [];
    const skillsCategories = [];
    const skillsBulletPoints = [];
    let skillsString = '';
    let skillsFormatted = '';

    Object.entries(skills).forEach(([category, skillList]) => {
      if (skillList && skillList.trim && skillList.trim()) {
        const categorySkills = skillList.split(',').map(s => s.trim()).filter(s => s);
        skillsCategories.push({
          category: category,
          skills: skillList,
          skillsArray: categorySkills
        });
        
        skillsBulletPoints.push({
          category: category,
          skills: skillList,
          formatted: `• ${category}: ${skillList}`
        });
        
        skillsArray.push(...categorySkills);
        skillsFormatted += `${category}: ${skillList}\n`;
        skillsString += skillList + ', ';
      }
    });

    skillsString = skillsString.replace(/, $/, '');
    skillsFormatted = skillsFormatted.trim();

    return {
      skillsArray: skillsArray,
      skillsString: skillsString,
      skillsFormatted: skillsFormatted,
      skillsCategories: skillsCategories,
      skillsList: skillsArray,
      skillsBulletPoints: skillsBulletPoints
    };
  }

  // If skills is an array
  if (Array.isArray(skills)) {
    return {
      skillsArray: skills,
      skillsString: skills.join(', '),
      skillsFormatted: skills.join(', '),
      skillsCategories: [{ category: 'Skills', skills: skills.join(', '), skillsArray: skills }],
      skillsList: skills,
      skillsBulletPoints: [{ category: 'Skills', skills: skills.join(', '), formatted: skills.join(', ') }]
    };
  }

  // Fallback
  return {
    skillsArray: [],
    skillsString: '',
    skillsFormatted: '',
    skillsCategories: [],
    skillsList: [],
    skillsBulletPoints: []
  };
}

  // FIXED: Helper method to format certifications
  formatCertifications(certifications) {
    console.log('Formatting certifications:', certifications, typeof certifications);
    
    // Return empty structure immediately if no certifications
    if (!certifications) {
      console.log('No certifications provided, returning empty structure');
      return {
        certificationsArray: [],
        certificationsString: '',
        certificationsFormatted: [],
        certificationsList: []
      };
    }

    // FIXED: Handle array format (this is your case)
    if (Array.isArray(certifications)) {
      console.log('Certifications is an array, processing each item...');
      
      const cleanedCertifications = certifications
        .map((cert, index) => {
          console.log(`Processing certification ${index}:`, cert, typeof cert);
          
          // Handle object format with name and link properties
          if (typeof cert === 'object' && cert !== null && cert.name) {
            const certName = cert.name;
            const certIssuer = cert.issuer || '';
            const certYear = cert.year || '';
            const certLink = cert.link || '';
            
            // Skip placeholder certifications
            if (certName === 'Professional Certification' || certLink === 'LINK') {
              return null;
            }
            
            let formattedCert = certName;
            if (certIssuer) formattedCert += ` - ${certIssuer}`;
            if (certYear) formattedCert += ` (${certYear})`;
            
            return formattedCert;
          }
          
          // Handle string format
          if (typeof cert === 'string') {
            const trimmed = cert.trim();
            // Skip placeholder certifications
            if (trimmed === 'Professional Certification' || !trimmed) {
              return null;
            }
            return trimmed;
          }
          
          return null;
        })
        .filter(cert => cert && cert.length > 0);
      
      console.log('Cleaned certifications array:', cleanedCertifications);
      
      return {
        certificationsArray: cleanedCertifications,
        certificationsString: cleanedCertifications.join(', '),
        certificationsFormatted: cleanedCertifications.map(cert => ({
          name: cert,
          issuer: '',
          year: '',
          link: '',
          display: cert
        })),
        certificationsList: cleanedCertifications
      };
    }

    // Handle string format
    if (typeof certifications === 'string') {
      const cleanedString = certifications.trim();
      
      if (!cleanedString || cleanedString.length === 0 || cleanedString === 'Professional Certification') {
        console.log('String was empty or placeholder, returning empty structure');
        return {
          certificationsArray: [],
          certificationsString: '',
          certificationsFormatted: [],
          certificationsList: []
        };
      }
      
      const certificationsArray = cleanedString.split(',').map(s => s.trim()).filter(s => s && s !== 'Professional Certification');
      
      return {
        certificationsArray: certificationsArray,
        certificationsString: cleanedString,
        certificationsFormatted: certificationsArray.map(cert => ({
          name: cert,
          issuer: '',
          year: '',
          link: '',
          display: cert
        })),
        certificationsList: certificationsArray
      };
    }

    // Fallback for unknown format
    console.log('Could not parse certifications, returning empty structure');
    return {
      certificationsArray: [],
      certificationsString: '',
      certificationsFormatted: [],
      certificationsList: []
    };
  }

  // Helper method to remove HTML tags and clean text
  removeHtmlTags(text) {
    if (!text) return '';
    return text
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&lt;b&gt;/g, '') // Remove encoded HTML
      .replace(/&lt;\/b&gt;/g, '') 
      .replace(/\\<b\\>/g, '') // Remove escaped HTML
      .replace(/\\<\/b\\>/g, '')
      .replace(/\<b\>/g, '') // Remove literal HTML text
      .replace(/\<\/b\>/g, '');
  }

  // Helper method to clean URLs and remove placeholder text
  cleanUrl(url) {
    if (!url || url.includes('Linkedin') || url.includes('Portfolio') || url === 'https://linkedin.com/in/Linkedin' || url === 'https://Portfolio') {
      return '';
    }
    return url;
  }

  // Helper method to format descriptions for templates
  formatDescription(description) {
    if (!description) return { bullets: [], formatted: '' };

    // FIXED: Ensure description is a string
    let descString = '';
    if (typeof description === 'string') {
      descString = description;
    } else if (Array.isArray(description)) {
      descString = description.join('\n');
    } else if (typeof description === 'object') {
      descString = JSON.stringify(description);
    } else {
      return { bullets: [], formatted: '' };
    }

    const bullets = descString
      .split('\n')
      .map(line => line.trim())
      .filter(line => line)
      .map(line => line.replace(/^[•·‣▪▫‪–'-]\s*/, ''));

    return {
      bullets: bullets,
      formatted: bullets.join('\n')
    };
  }

// UPDATED: prepareTemplateData method in templateService.js with better education handling

prepareTemplateData(resumeData) {
  console.log('Preparing template data from:', resumeData);
  
  // Handle skills formatting - use actual extracted data
  const skillsData = this.formatSkills(resumeData.skills);
  console.log('Skills data formatted:', skillsData);
  
  // Handle certifications formatting - use actual extracted data only
  const certificationsData = this.formatCertifications(resumeData.certifications);
  console.log('Certifications data formatted:', certificationsData);

  // Handle work experience
  let workExperiences = [];
  if (resumeData.workExperiences) {
    workExperiences = resumeData.workExperiences;
  } else if (resumeData.workExperience) {
    workExperiences = resumeData.workExperience;
  }
  
  const formattedWorkExperience = workExperiences.map(exp => {
    const descData = this.formatDescription(this.removeHtmlTags(exp.description || ''));
    return {
      position: exp.position || '',
      company: exp.company || '',
      duration: exp.duration || '',
      description: this.removeHtmlTags(exp.description || ''),
      bullets: descData.bullets,
      formattedDescription: descData.formatted
    };
  });

  // Handle projects formatting
  const projects = resumeData.projects || [];
  const formattedProjects = projects.map(proj => {
    const descData = this.formatDescription(this.removeHtmlTags(proj.description || ''));
    return {
      name: proj.name || '',
      technologies: proj.technologies || '',
      description: this.removeHtmlTags(proj.description || ''),
      duration: proj.duration || '',
      bullets: descData.bullets,
      formattedDescription: descData.formatted,
      projectName: proj.name || '',
      projectDescription: descData.formatted
    };
  });

  // FIXED: Handle education - better logic
  let education = [];
  if (resumeData.education && Array.isArray(resumeData.education) && resumeData.education.length > 0) {
    education = resumeData.education;
  } else if (resumeData.educationList && Array.isArray(resumeData.educationList) && resumeData.educationList.length > 0) {
    education = resumeData.educationList;
  }
  
  // FIXED: Filter out empty education entries and format properly
  const validEducation = education.filter(edu => 
    edu && 
    (edu.institution && edu.institution.trim() && edu.institution !== '') &&
    (edu.degree && edu.degree.trim() && edu.degree !== '')
  );
  
  console.log('Valid education entries for template:', validEducation);
  
  const formattedEducation = validEducation.map(edu => {
    // Create comprehensive education object for template
    const formattedEdu = {
      degree: edu.degree || '',
      field: edu.field || '',
      institution: edu.institution || '',
      year: edu.year || '',
      location: edu.location || '',
      // Combined formats for different template needs
      combined: `${edu.degree || ''}${edu.field ? ' in ' + edu.field : ''}`.trim(),
      
      // FIXED: Ensure all parts are strings before calling .trim()
      fullEducation: [
        `${edu.degree || ''}${edu.field ? ' in ' + edu.field : ''}`.trim(),
        edu.institution || '', 
        edu.year || ''
      ]
        .filter(part => part && typeof part === 'string' && part.trim())  // ← FIXED: Type check added
        .join(', '),
      
      // Individual components for template flexibility
      degreeAndField: `${edu.degree || ''}${edu.field ? ' in ' + edu.field : ''}`.trim(),
      institutionAndYear: `${edu.institution || ''}${edu.year ? ' (' + edu.year + ')' : ''}`.trim()
    };
    
    console.log('Formatted education entry:', formattedEdu);
    return formattedEdu;
  });

  const templateData = {
    // Personal Information
    fullName: resumeData.personalInfo?.name || resumeData.fullName || '',
    name: resumeData.personalInfo?.name || resumeData.name || resumeData.fullName || '',
    email: resumeData.personalInfo?.email || resumeData.email || '',
    phone: resumeData.personalInfo?.phone || resumeData.phone || '',
    location: resumeData.personalInfo?.location || resumeData.location || '',
    linkedin: this.cleanUrl(resumeData.personalInfo?.linkedin || resumeData.linkedin || ''),
    portfolio: this.cleanUrl(resumeData.personalInfo?.portfolio || resumeData.portfolio || ''),

    // Professional Summary
    professionalSummary: this.removeHtmlTags(resumeData.summary || resumeData.professionalSummary || ''),
    summary: this.removeHtmlTags(resumeData.summary || resumeData.professionalSummary || ''),

    // Work Experience
    workExperiences: formattedWorkExperience,
    workExperience: formattedWorkExperience,

    // FIXED: Education with better handling
    education: formattedEducation,
    educationList: formattedEducation,
    educationEntries: formattedEducation, // Alternative naming for templates
    hasEducation: formattedEducation.length > 0,
    educationCount: formattedEducation.length,

    // UPDATED: Skills with bullet point formatting
    skills: skillsData.skillsArray,
    skillsString: skillsData.skillsString,
    skillsFormatted: skillsData.skillsFormatted,
    skillsCategories: skillsData.skillsCategories,
    skillsCategorized: skillsData.skillsFormatted,
    skillsList: skillsData.skillsList,
    skillsBulletPoints: skillsData.skillsBulletPoints || [],
    skillsCategorizedBullets: skillsData.skillsBulletPoints || [],

    // Projects
    projects: formattedProjects,

    // Certifications
    certifications: certificationsData.certificationsArray,
    certificationsString: certificationsData.certificationsString,
    certificationsFormatted: certificationsData.certificationsFormatted,
    certificationsList: certificationsData.certificationsList,
    certificationsArray: certificationsData.certificationsArray,

    // Helper flags
    hasWorkExperience: formattedWorkExperience.length > 0 && 
      formattedWorkExperience.some(exp => exp.company || exp.position),
    hasProjects: formattedProjects.length > 0 && 
      formattedProjects.some(proj => proj.name),
    hasSkills: skillsData.skillsArray.length > 0 || skillsData.skillsString.length > 0,
    hasCertifications: certificationsData.certificationsArray.length > 0,
    hasSkillsBulletPoints: (skillsData.skillsBulletPoints || []).length > 0,

    // Current date for dynamic content
    currentDate: new Date().toLocaleDateString(),
    currentYear: new Date().getFullYear()
  };

  console.log('Final template data prepared:');
  console.log('- Education entries:', templateData.education);
  console.log('- Has Education:', templateData.hasEducation);
  console.log('- Education Count:', templateData.educationCount);
  
  return templateData;
}

  // Get available templates
  getAvailableTemplates() {
    return [
      {
        id: 1,
        name: 'Professional',
        description: 'Clean and professional layout suitable for most industries',
        filename: 'professional.docx'
      },
      {
        id: 2,
        name: 'Modern',
        description: 'Contemporary design with a focus on skills and achievements',
        filename: 'modern.docx'
      },
      {
        id: 3,
        name: 'Creative',
        description: 'Eye-catching design for creative fields and portfolios',
        filename: 'creative.docx'
      },
      {
        id: 4,
        name: 'Academic',
        description: 'Formal layout emphasizing education and publications',
        filename: 'academic.docx'
      }
    ];
  }

  // Function to get template file name (for UI display)
  getTemplateFileName(templateId) {
    const templateFiles = {
      1: 'professional.docx',
      2: 'modern.docx',
      3: 'creative.docx',
      4: 'academic.docx'
    };
    
    return templateFiles[templateId] || 'professional.docx';
  }

  // Clear template cache (useful for development)
  clearCache() {
    this.templateCache.clear();
  }

  // Debug method to check resume data processing
  debugResumeData(resumeData) {
    console.log('\n=== RESUME DATA DEBUG ===');
    console.log('Full resume data:', resumeData);
    console.log('\nPersonal Info:', resumeData.personalInfo);
    console.log('\nSkills:', resumeData.skills, typeof resumeData.skills);
    console.log('\nCertifications:', resumeData.certifications, typeof resumeData.certifications);
    console.log('\nEducation:', resumeData.education, typeof resumeData.education);
    console.log('\nWork Experience:', resumeData.workExperience?.length || 0, 'entries');
    console.log('\nProjects:', resumeData.projects?.length || 0, 'entries');
    console.log('=== END DEBUG ===\n');
  }
}

// Singleton instance
const templateService = new TemplateService();
export default templateService;